// src/pages/Login.js

import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import WalletsAPI from '../api/WalletsAPI';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useRateLimit from '../hooks/useRateLimit';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  EmailOutlined,
  LockOutlined,
  VisibilityOutlined,
  VisibilityOffOutlined,
  LoginOutlined,
  AutoAwesomeOutlined,
} from '@mui/icons-material';

// Custom Google Icon Component
function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

// WhatsApp Icon Component
function WhatsAppIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#25D366"
        d="M12.031 2C6.501 2 2.018 6.482 2.018 12.011c0 1.752.452 3.397 1.241 4.831L2 22l5.309-1.244c1.393.726 2.972 1.14 4.64 1.14 5.53 0 10.013-4.482 10.013-10.011C22.044 6.482 17.561 2 12.031 2zm5.614 14.223c-.236.664-1.39 1.291-1.914 1.337-.084.008-.168.012-.252.012-.434 0-.862-.103-1.273-.303-1.195-.584-2.235-1.265-3.153-2.084-1.697-1.515-2.8-3.486-2.876-3.615-.073-.126-.601-1.045-.601-2.001 0-.952.372-1.416.512-1.605.116-.158.358-.398.74-.398.072 0 .137.005.196.009.144.01.343.015.537.415.237.486.804 1.958.874 2.1.073.141.121.306.025.489-.096.186-.144.301-.287.468-.141.163-.297.366-.423.491-.135.132-.274.275-.117.541.156.263.697 1.147 1.495 1.859 1.027.916 1.892 1.202 2.161 1.338.267.135.423.113.579-.071.155-.182.669-.779.847-1.045.178-.267.356-.223.599-.134.244.089 1.547.729 1.812.861.267.134.445.2.512.31.066.11.066.641-.171 1.304z"
      />
    </svg>
  );
}

// Telegram Icon Component
function TelegramIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#0088cc"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.56c-.21 2.27-1.13 7.75-1.6 10.28-.2 1.07-.59 1.43-.97 1.46-.83.06-1.46-.54-2.26-1.07-1.26-.82-1.96-1.33-3.18-2.13-1.41-.93-.5-1.44.31-2.27.21-.22 3.94-3.61 4.01-3.92.01-.04.02-.17-.07-.24-.09-.07-.22-.04-.31-.02-.13.02-2.29 1.46-6.47 4.29-.61.42-1.17.63-1.66.62-.55-.01-1.6-.31-2.38-.56-.96-.31-1.72-.48-1.65-1.01.03-.28.42-.56 1.15-.85 4.49-1.96 7.49-3.25 8.99-3.88 4.28-1.79 5.17-2.1 5.75-2.11.13 0 .41.03.59.17.15.12.19.28.21.44-.01.05.01.25 0 .39z"
      />
    </svg>
  );
}

// Animated Background Component
function AnimatedBackground() {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {/* Gradient Orbs */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, -100, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      
      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, 100, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: 500,
          height: 500,
          background: 'radial-gradient(circle, rgba(14, 165, 233, 0.3) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

    </Box>
  );
}

// Social Login Button Component
function SocialLoginButton({ icon, provider, onClick }) {
  return (
    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
      <Button
        fullWidth
        variant="outlined"
        startIcon={icon}
        onClick={onClick}
        sx={{
          py: 1.5,
          borderColor: 'var(--border-primary)',
          color: 'var(--text-primary)',
          textTransform: 'none',
          fontSize: '16px',
          fontWeight: 500,
          '&:hover': {
            borderColor: 'var(--primary-blue)',
            background: 'rgba(59, 130, 246, 0.05)',
          },
        }}
      >
        Continue with {provider}
      </Button>
    </motion.div>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const navigate = useNavigate();
  const { currentUser, role } = useAuth();
  
  // Rate limiting - 5 attempts per 15 minutes
  const { canAttempt, attemptAction, remainingAttempts } = useRateLimit('login', 5, 15 * 60 * 1000);

  useEffect(() => {
    // If user is already logged in, redirect to appropriate dashboard
    if (currentUser) {
      navigate(role === 'admin' ? '/admin' : '/dashboard');
    }
  }, [currentUser, role, navigate]);

  const validateForm = () => {
    const errors = {};
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      const provider = new GoogleAuthProvider();
      
      // הגדרת פרמטרים נוספים לפופאפ
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Create user profile for new Google users
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          createdAt: new Date().toISOString(),
          authProvider: 'google'
        });
        
        // Register with wallet API
        try {
          await WalletsAPI.registerUserAdmin(user.email, user.displayName);
        } catch (walletError) {
        }
      }
      
      // Navigate based on user type
      const updatedUserDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = updatedUserDoc.data();
      
      if (userData?.isAdmin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      setError(
        error.code === 'auth/popup-closed-by-user' 
          ? 'Sign-in cancelled' 
          : 'Failed to sign in with Google. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Check rate limit
    if (!canAttempt) {
      setError(`Too many login attempts. ${remainingAttempts} attempts remaining. Please try again later.`);
      return;
    }
    
    // Record attempt
    if (!attemptAction()) {
      setError('Too many login attempts. Please try again in 15 minutes.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;

      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);

      let userRole = 'customer';
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.role?.toLowerCase() === 'admin') {
          userRole = 'admin';
        }
      }

      // Success animation before redirect
      await new Promise(resolve => setTimeout(resolve, 500));
      
      navigate(userRole === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      // Error logging removed for security
      setError(
        err.code === 'auth/user-not-found' 
          ? 'No account found with this email'
          : err.code === 'auth/wrong-password'
          ? 'Incorrect password'
          : 'Failed to login. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        p: 2,
      }}
    >
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 1 }}
      >
        <Box
          className="glass-card"
          component="form"
          onSubmit={handleLogin}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, type: "spring" }}
            >
              <AutoAwesomeOutlined 
                sx={{ 
                  fontSize: 64,
                  color: 'var(--primary-blue)',
                  filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.5))',
                  mb: 2,
                }} 
              />
            </motion.div>
            
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontFamily: 'Space Grotesk, sans-serif',
                background: 'linear-gradient(45deg, var(--primary-blue), var(--primary-sky))',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Welcome Back
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
              Sign in to continue to CardsPay
            </Typography>
          </Box>

          {/* Error Alert */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Alert 
                  severity="error" 
                  onClose={() => setError('')}
                  sx={{ mb: 3 }}
                >
                  {error}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form Fields */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors({ ...fieldErrors, email: '' });
              }}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              inputProps={{ maxLength: 100 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'var(--bg-secondary)',
                  '&:hover fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors({ ...fieldErrors, password: '' });
              }}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              inputProps={{ maxLength: 50, autoComplete: 'current-password' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'var(--text-secondary)' }}
                    >
                      {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'var(--bg-secondary)',
                  '&:hover fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                },
              }}
            />
          </Box>

          {/* Remember Me & Forgot Password */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: 'var(--text-secondary)',
                    '&.Mui-checked': {
                      color: 'var(--primary-blue)',
                    },
                  }}
                />
              }
              label="Remember me"
              sx={{ color: 'var(--text-secondary)' }}
            />
            
            <Link
              to="/forgot"
              style={{
                color: 'var(--primary-blue)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Forgot password?
            </Link>
          </Box>

          {/* Login Button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              fullWidth
              type="submit"
              disabled={loading}
              startIcon={loading ? null : <LoginOutlined />}
              sx={{
                py: 2,
                mb: 3,
                background: 'linear-gradient(45deg, var(--primary-blue), var(--primary-sky))',
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  boxShadow: '0 12px 48px rgba(59, 130, 246, 0.4)',
                },
                '&:disabled': {
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                },
              }}
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <AutoAwesomeOutlined />
                </motion.div>
              ) : (
                'Sign In'
              )}
            </Button>
          </motion.div>

          {/* Divider */}
          <Divider sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', px: 2 }}>
              OR CONTINUE WITH
            </Typography>
          </Divider>

          {/* Social Login */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <Box sx={{ flex: 1 }}>
              <SocialLoginButton
                icon={<GoogleIcon />}
                provider="Google"
                onClick={handleGoogleSignIn}
              />
            </Box>
          </Box>

          {/* Sign Up Link - More Prominent */}
          <Box sx={{ 
            textAlign: 'center',
            mt: 3,
            p: 3,
            borderRadius: 2,
            bgcolor: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}>
            <Typography
              variant="body2"
              sx={{
                color: 'var(--text-primary)',
                mb: 1,
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              Don't have an account yet? Sign up now
            </Typography>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                component={Link}
                to="/register"
                variant="outlined"
                fullWidth
                sx={{
                  py: 1.5,
                  borderColor: 'var(--primary-blue)',
                  color: 'var(--primary-blue)',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'var(--primary-blue)',
                    background: 'rgba(59, 130, 246, 0.1)',
                  },
                }}
              >
                Create Free Account
              </Button>
            </motion.div>
          </Box>

          {/* Support Buttons */}
          <Box sx={{ 
            mt: 4,
            pt: 3,
            borderTop: '1px solid var(--border-primary)',
            textAlign: 'center'
          }}>
            <Typography
              variant="caption"
              sx={{
                color: 'var(--text-secondary)',
                display: 'block',
                mb: 2
              }}
            >
              Need Help? Contact Support
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outlined"
                  startIcon={<TelegramIcon />}
                  onClick={() => window.open('https://t.me/CardsPay_Support', '_blank')}
                  sx={{
                    borderColor: '#0088cc',
                    color: '#0088cc',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    fontSize: '16px',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#0088cc',
                      bgcolor: 'rgba(0, 136, 204, 0.1)',
                    },
                  }}
                >
                  Telegram Support
                </Button>
              </motion.div>
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
